import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router';
import { ToastProvider } from './components/ToastContext';
import './index.css';

// Apply dark mode early (before first paint) so the login page — which does not
// mount the Layout — respects the stored/system preference too.
(function applyDarkMode() {
  const saved = localStorage.getItem('darkMode');
  const dark = saved !== null
    ? saved === 'true'
    : window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (dark) document.documentElement.classList.add('dark');
})();

// --- Global auth guard: token expired/invalid → try refresh once → reload with
// fresh token, or clean logout. Prevents the app shell from rendering with dead
// credentials (empty skeleton) when any API call comes back 401.
const originalFetch: typeof window.fetch = window.fetch.bind(window);

let refreshing = false;

export async function refreshSession(): Promise<boolean> {
  const rt = localStorage.getItem('refreshToken');
  if (!rt) return false;
  if (refreshing) {
    await new Promise((r) => setTimeout(r, 500));
    return !!localStorage.getItem('accessToken');
  }
  refreshing = true;
  try {
    const res = await originalFetch('/api/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
      },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!res.ok) return false;
    const d = await res.json();
    localStorage.setItem('accessToken', d.accessToken);
    localStorage.setItem('refreshToken', d.refreshToken);
    return true;
  } catch {
    return false;
  } finally {
    refreshing = false;
  }
}

function clearSession() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('tenantSlug');
}

window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const res = await originalFetch(input, init);
  if (res.status === 401 && typeof input === 'string') {
    const url = input;
    // Never intercept the auth endpoints themselves (login/refresh with bad
    // credentials must surface their own error).
    const isAuthCall =
      url.includes('/auth/login') || url.includes('/auth/refresh') ||
      url.includes('/sysadmin/login') || url.includes('/api/auth/me');
    if (url.includes('/api/') && !isAuthCall) {
      // SysAdmin global session (separate token, no tenant).
      if (url.includes('/sysadmin/')) {
        localStorage.removeItem('sysadminToken');
        localStorage.removeItem('sysadminRefreshToken');
        localStorage.removeItem('sysadminUser');
        window.location.href = '/sysadmin/login';
        return res;
      }
      const ok = await refreshSession();
      if (ok) {
        // Re-mount the SPA with the fresh token — no stale skeleton visible.
        window.location.reload();
      } else {
        clearSession();
        window.location.href = '/login';
      }
    }
  }
  return res;
};

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

// --- Google OAuth redirect capture (before React mounts) ---
// Google redirects to <origin>#id_token=...&state=... which lands on '/'
// (protected → Login route). The hash fragment is lost when ProtectedLayout
// does window.location.href = '/login', so we persist it to sessionStorage
// BEFORE React/routing runs, and Login reads it on mount.
(function captureGoogleRedirect() {
  try {
    const hash = window.location.hash;
    if (!hash) return;
    const params = new URLSearchParams(hash.slice(1));
    const idToken = params.get('id_token') || params.get('access_token') || '';
    const error = params.get('error');
    if (error) {
      sessionStorage.setItem('googleOAuthError', error);
    }
    if (idToken) {
      let slug = '';
      const stateRaw = params.get('state');
      if (stateRaw) {
        try { slug = JSON.parse(atob(stateRaw)).slug || ''; } catch { /* ignore */ }
      }
      sessionStorage.setItem('googleIdToken', idToken);
      if (slug) sessionStorage.setItem('googleOAuthSlug', slug);
    }
    if (idToken || error) {
      // Clean the URL fragment so a reload doesn't re-trigger
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
    }
  } catch { /* ignore */ }
})();

createRoot(root).render(
  <ToastProvider>
    <RouterProvider router={router} />
  </ToastProvider>
);
